import { getLinkIdOfQuestionnaireQuestion } from '../utils/questionnaire-engine.utils'

import type { QuestionnaireEngineState } from '../types/questionnaire-engine.types'

// ================================
// Progress Selectors
// ================================
/**
 *
 * @param state - The state of the questionnaire engine
 * @returns The current linkId from navigationHistory
 */
const selectCurrentLinkId = (state: QuestionnaireEngineState) => {
  const { historyCursor, navigationHistory } = state

  if (navigationHistory.length === 0) return null
  if (historyCursor < 0 || historyCursor >= navigationHistory.length) return null

  return navigationHistory[historyCursor] ?? null
}

/**
 * Selects the current question based on the current linkId
 * @param state - The state of the questionnaire engine
 * @returns The current question
 */
export const selectCurrentQuestion = (state: QuestionnaireEngineState) => {
  const linkId = selectCurrentLinkId(state)
  if (!linkId) return null

  return state.answerableQuestions.find((q) => getLinkIdOfQuestionnaireQuestion(q) === linkId) ?? null
}

/**
 *
 * @param state - The state of the questionnaire engine
 * @returns The current question index
 */
export const selectCurrentQuestionIndex = (state: QuestionnaireEngineState) => {
  const q = selectCurrentQuestion(state)
  if (!q) return -1

  const linkId = getLinkIdOfQuestionnaireQuestion(q)
  return state.answerableQuestions.findIndex((qq) => getLinkIdOfQuestionnaireQuestion(qq) === linkId)
}

/**
 *
 * @param state - The state of the questionnaire engine
 * @returns The total number of questions
 */
export const selectTotalQuestions = (state: QuestionnaireEngineState) => state.answerableQuestions.length

/**
 * Returns the count of all non-Display items in questionnaire.item.
 *
 * Use this as the constant denominator for linear-flow progress. It never
 * changes based on answers — conditional (skipped) questions are still counted.
 */
export const selectMaxPossibleQuestions = (state: QuestionnaireEngineState) =>
  state.questionnaire.item.filter((q) => q.type !== 'Display').length

/**
 * Returns the 0-based position of the current question in the full
 * questionnaire item list (skipping Display items).
 *
 * Use this as the numerator for linear-flow progress. Questions that are
 * skipped due to conditions but appear before the current question in the
 * original list are implicitly counted — they contribute to the index without
 * needing explicit tracking.
 *
 * For mid-questionnaire Display items (info screens between real questions),
 * falls back to the last visited non-Display item's position so the progress
 * bar holds steady instead of jumping to 0%.
 *
 * Returns -1 if no non-Display item has been visited yet (e.g. intro screen).
 */
export const selectCurrentGlobalIndex = (state: QuestionnaireEngineState): number => {
  const linkId = selectCurrentLinkId(state)
  if (!linkId) return -1

  const nonDisplayItems = state.questionnaire.item.filter((q) => q.type !== 'Display')
  const directIndex = nonDisplayItems.findIndex((item) => getLinkIdOfQuestionnaireQuestion(item) === linkId)
  if (directIndex !== -1) return directIndex

  // Current item is a Display type — search backward in history for the last
  // visited non-Display item so progress doesn't jump to 0% mid-questionnaire.
  for (let index = state.historyCursor - 1; index >= 0; index--) {
    const previousLinkId = state.navigationHistory[index]
    const index_ = nonDisplayItems.findIndex((item) => getLinkIdOfQuestionnaireQuestion(item) === previousLinkId)
    if (index_ !== -1) return index_
  }

  return -1
}

// ================================
// Submission Selectors
// ================================
/**
 * Selects if the questionnaire can be submitted
 * Meaning all questions answers are valid and the questionnaire is complete
 * @param state
 * @returns
 */
export const selectCanSubmit = (state: QuestionnaireEngineState) =>
  Object.values(state.validations).every((v: { isValid: boolean }) => v.isValid)

// ================================
// Navigation Selectors
// ================================
/**
 * Selects if the questionnaire can go back
 * @param state - The state of the questionnaire engine
 * @returns True if the questionnaire can go back
 */
export const selectCanGoBack = (state: QuestionnaireEngineState) => {
  // History is forward (oldest to newest), cursor is direct index
  // Can go back if cursor > 0 (not at the first/oldest question)
  if (state.navigationHistory.length === 0) return false
  if (state.historyCursor <= 0) return false

  // deeplink guard (cannot go before initial deeplink)
  if (state.initialDeeplinkQuestion) {
    const first = state.navigationHistory[0]
    if (first === state.initialDeeplinkQuestion && state.historyCursor === 0) {
      return false
    }
  }

  return true
}

/**
 * Selects if the questionnaire can go next
 * @param state - The state of the questionnaire engine
 * @returns True if the questionnaire can go next
 */
export const selectCanGoNext = (state: QuestionnaireEngineState) => {
  const canSubmit = selectCanSubmit(state)

  if (state.mode === 'reconfirm') {
    // reconfirm keeps going until valid
    return !canSubmit
  }

  const currentIndex = selectCurrentQuestionIndex(state)
  return currentIndex > -1 && currentIndex < state.answerableQuestions.length - 1
}
